<template>
  <section class="mt-5 mb-5">
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-md-8">

          <!-- Custom sticky tabs -->
          <div class="sticky-tabs">
            <div class="tabs" role="tablist" aria-label="Albums tabs">
              <button
                role="tab"
                class="tab"
                :class="{ 'is-active': activeTab === 'my' }"
                :aria-selected="activeTab === 'my' ? 'true' : 'false'"
                :tabindex="activeTab === 'my' ? 0 : -1"
                @click="switchTab('my')"
                @keyup.enter.space="switchTab('my')"
              >
                My Events
              </button>
              <button
                role="tab"
                class="tab"
                :class="{ 'is-active': activeTab === 'shared' }"
                :aria-selected="activeTab === 'shared' ? 'true' : 'false'"
                :tabindex="activeTab === 'shared' ? 0 : -1"
                @click="switchTab('shared')"
                @keyup.enter.space="switchTab('shared')"
              >
                Shared With Me
              </button>
              <span
                class="active-rail"
                :style="{
                  transform: activeTab === 'my' ? 'translateX(0%)' : 'translateX(100%)'
                }"
                aria-hidden="true"
              ></span>
            </div>
          </div>

          <!-- Loading Skeleton -->
          <div v-if="loading" class="skeleton-container" aria-label="Loading albums">
            <div 
              v-for="i in 4" 
              :key="`skeleton-${i}`" 
              class="skeleton-card"
            >
              <div class="skeleton-image"></div>
              <div class="skeleton-content">
                <div class="skeleton-title"></div>
                <div class="skeleton-text"></div>
                <div class="skeleton-text short"></div>
                <div class="skeleton-meta">
                  <div class="skeleton-badge"></div>
                  <div class="skeleton-badge"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- My Albums -->
          <div v-else-if="activeTab === 'my'" aria-labelledby="my-albums-heading">
            <h2 id="my-albums-heading" class="sr-only">My Albums</h2>
            <AlbumCard
              v-for="event in myAlbums"
              :key="`my-${event.id}`"
              :event="{ ...event, resolved_id: event.id, resolved_type: event.photo_booth_type }"
              type="my"
              :session-user-id="String(session.userId || '')"
            />
            <div v-if="!myAlbums.length" class="empty"> 
              <div class="row mt-4">
                <div class="col-12 text-center py-5">
                  <img v-no-right-click 
                    src="/assets/placeholders/no-albums.png" 
                    alt="No albums" 
                    style="max-width: 240px;" 
                    class="mb-3"
                    @error="handleImageError"
                  >  
                  <p class="text-muted">You haven't created any albums yet</p>
                  <router-link to="/create-event" class="btn btn-primary mt-2">
                    Create Your First Event
                  </router-link>
                </div>
              </div>
            </div>
          </div>

          <!-- Shared Albums -->
          <div v-else-if="activeTab === 'shared'" aria-labelledby="shared-albums-heading">
            <h2 id="shared-albums-heading" class="sr-only">Shared Albums</h2>
            <AlbumCard
              v-for="event in receivedAlbums"
              :key="`shared-${event.eventid}`"
              :event="{
                ...event,
                resolved_id: event.eventid,
                resolved_type: event.type_event
              }"
              type="shared"
            />
            <div v-if="!receivedAlbums.length" class="empty">
              <div class="row mt-4">
                <div class="col-12 text-center py-5">
                  <img v-no-right-click 
                    src="/assets/placeholders/no-albums.png" 
                    alt="No shared albums" 
                    style="max-width: 240px;" 
                    class="mb-3"
                    @error="handleImageError"
                  >  
                  <p class="text-muted">No albums have been shared with you yet</p>
                  <p class="text-muted small">When someone shares an album with you, it will appear here</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useHead } from '@vueuse/head'
import { useSessionStore } from '@/stores/session'
import api from '@/services/api'
import AlbumCard from '@/components/common/AlbumCard.vue'

useHead({
  title: 'Albums - Invi8',
  meta: [
    { name: 'description', content: 'Albums you created and albums shared with you.' },
    { name: 'keywords', content: 'albums, invites, photos, events' },
  ],
})

const session = useSessionStore()

const activeTab = ref('my')
const myAlbums = ref([])
const receivedAlbums = ref([])
const loading = ref(false)
let loadingTimeout = null

// Handle broken images
const handleImageError = (event) => {
  const target = event.target
  target.style.display = 'none'
  const parent = target.parentElement
  if (parent) {
    const icon = document.createElement('i')
    icon.className = 'fas fa-images fa-3x mb-3 text-light'
    icon.setAttribute('aria-hidden', 'true')
    parent.appendChild(icon)
  }
}

async function getAlbums() {
  try {
    const formData = new FormData()
    formData.append('id', session.userId)
    const response = await api.getAlbums(formData)
    
    // Handle different response structures
    if (response?.status || response?.data?.status) {
      myAlbums.value = response.data?.data || response.data || []
    } else {
      myAlbums.value = []
    }
  } catch (error) {
    console.error('Error fetching albums:', error)
    myAlbums.value = []
  }
}

async function getReceivedAlbums() {
  try {
    const formData = new FormData()
    formData.append('id', session.userId)
    const response = await api.getReceivedAlbums(formData)
    
    // Handle different response structures
    if (response?.status || response?.data?.status) {
      receivedAlbums.value = response.data?.list || response.data || []
    } else {
      receivedAlbums.value = []
    }
  } catch (error) {
    console.error('Error fetching received albums:', error)
    receivedAlbums.value = []
  }
}

async function switchTab(tab) {
  if (activeTab.value === tab) return
  
  // Clear any existing timeout
  if (loadingTimeout) {
    clearTimeout(loadingTimeout)
    loadingTimeout = null
  }
  
  activeTab.value = tab
  loading.value = true

  // Set minimum loading time for better UX (500ms)
  const minLoadingTime = new Promise(resolve => {
    loadingTimeout = setTimeout(resolve, 500)
  })

  try {
    const apiCall = tab === 'my' ? getAlbums() : getReceivedAlbums()
    await Promise.all([apiCall, minLoadingTime])
  } catch (error) {
    console.error(`Error loading ${tab} albums:`, error)
  } finally {
    loading.value = false
    if (loadingTimeout) {
      clearTimeout(loadingTimeout)
      loadingTimeout = null
    }
  }
}

// Initial load
onMounted(async () => {
  loading.value = true
  
  const minLoadingTime = new Promise(resolve => {
    loadingTimeout = setTimeout(resolve, 500)
  })

  try {
    await Promise.all([getAlbums(), minLoadingTime])
  } catch (error) {
    console.error('Error loading initial albums:', error)
  } finally {
    loading.value = false
    if (loadingTimeout) {
      clearTimeout(loadingTimeout)
      loadingTimeout = null
    }
  }
})

// Cleanup
onUnmounted(() => {
  if (loadingTimeout) {
    clearTimeout(loadingTimeout)
    loadingTimeout = null
  }
})
</script>

<style scoped>
/* Sticky wrapper to keep tabs visible below header */
.sticky-tabs {
  position: sticky;
  top: 70px;
  z-index: 20;
  background: #fff;
  padding-top: .25rem;
  padding-bottom: .25rem;
  margin-bottom: 1rem;
}

/* Tabs container */
.tabs {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  border: 1px solid #FB721020;
  border-radius: .75rem;
  background: #fff;
  overflow: hidden;
}

/* The moving rail (active highlight) */
.active-rail {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 50%;
  background: #e9f2ff;
  border-right: 1px solid #FB721020;
  border-left: 1px solid #FB721020;
  transition: transform .25s ease;
  z-index: 0;
}

/* Individual tab */
.tab {
  position: relative;
  z-index: 1;
  appearance: none;
  border: 0;
  background: transparent;
  padding: .7rem 1rem;
  font-weight: 700;
  font-size: .95rem;
  color: #FB7210;
  cursor: pointer;
  outline: none;
  user-select: none;
  transition: background-color 0.2s ease;
}

.tab:hover { 
  background: rgba(251, 114, 16, 0.06); 
}

.tab.is-active { 
  color: #FB7210; 
}

/* Screen reader only */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Skeleton Loader Styles */
.skeleton-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
}

.skeleton-card {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid #f0f0f0;
  border-radius: 12px;
  background: #fff;
}

.skeleton-image {
  width: 100px;
  height: 100px;
  border-radius: 8px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s ease-in-out infinite;
}

.skeleton-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.skeleton-title {
  height: 20px;
  width: 70%;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s ease-in-out infinite;
}

.skeleton-text {
  height: 14px;
  width: 90%;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s ease-in-out infinite;
}

/* Gradient hover cards */
:deep(.event-card) {
  --border-width: 3px;
  --fill: 0deg;
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  background: linear-gradient(135deg, #fff7ef 0%, #fff3e6 48%, #ffe9d7 100%);
  border: var(--border-width) solid transparent;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08);
  transition: transform .18s ease, box-shadow .18s ease;
}
:deep(.event-card:hover) {
  transform: translateY(-3px);
  box-shadow: 0 28px 60px rgba(15, 23, 42, 0.12);
}
:deep(.event-card)::before,
:deep(.event-card)::after {
  content: '';
  position: absolute;
  inset: calc(var(--border-width) * -1);
  border-radius: inherit;
  z-index: 0;
  pointer-events: none;
}
:deep(.event-card)::before {
  background: #fff;
}
:deep(.event-card)::after {
  background: conic-gradient(from -90deg, #ff7a1a 0deg, #ff9a3c var(--fill), transparent var(--fill), transparent 360deg);
  padding: var(--border-width);
  -webkit-mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  mask-composite: exclude;
  opacity: 0;
  transition: opacity .25s ease, filter .25s ease;
  filter: drop-shadow(0 8px 18px rgba(249, 115, 22, 0.25));
}
:deep(.event-card:hover)::after {
  opacity: 1;
  animation: fillBorderAlbum .9s ease forwards;
  filter: drop-shadow(0 12px 26px rgba(249, 115, 22, 0.32));
}

@property --fill {
  syntax: '<angle>';
  inherits: false;
  initial-value: 0deg;
}

@keyframes fillBorderAlbum {
  from { --fill: 0deg; }
  to { --fill: 360deg; }
}

.skeleton-text.short {
  width: 60%;
}

.skeleton-meta {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.skeleton-badge {
  height: 24px;
  width: 80px;
  border-radius: 12px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s ease-in-out infinite;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Empty states */
.empty {
  text-align: center;
  color: #6b7280;
  padding: 1.25rem 0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .sticky-tabs {
    top: 60px; /* Adjust for mobile header */
  }
  
  .skeleton-card {
    flex-direction: column;
    text-align: center;
  }
  
  .skeleton-image {
    width: 100%;
    height: 120px;
    margin: 0 auto;
  }
  
  .skeleton-content {
    align-items: center;
  }
  
  .skeleton-title,
  .skeleton-text {
    width: 100%;
  }
  
  .skeleton-text.short {
    width: 80%;
  }
  
  .tabs {
    grid-template-columns: 1fr 1fr;
  }
  
  .tab {
    padding: 0.6rem 0.5rem;
    font-size: 0.9rem;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .active-rail {
    transition: none;
  }
  
  .skeleton-image,
  .skeleton-title,
  .skeleton-text,
  .skeleton-badge {
    animation: none;
    background: #f0f0f0;
  }
}
</style>
